import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers,
  Activity,
  Server,
  HardDrive,
  FileText,
  Search,
  Cpu,
  Clock,
  AlertTriangle,
  Loader2,
  Lock,
  Network,
  Camera,
  Recycle,
  ArrowUpDown,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface OpenSearchIndicesViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
}

const PALETTE = [
  '#10b981', // emerald
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#f59e0b', // amber
  '#a855f7', // purple
  '#ef4444', // red
  '#ec4899', // pink
  '#84cc16', // lime
];

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 max-h-12 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 dark:text-gray-200 text-[9px] truncate max-w-[140px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : (entry.value ?? '-')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const OpenSearchIndicesView: React.FC<OpenSearchIndicesViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);

  // Raw API datasets
  const [searchStats, setSearchStats] = useState<any>(null);
  const [indexStats, setIndexStats] = useState<any>(null);
  const [sizeStats, setSizeStats] = useState<any>(null);
  const [clusterStats, setClusterStats] = useState<any>(null);

  // Tables datasets
  const [nodeTableData, setNodeTableData] = useState<any[]>([]);
  const [shardTableData, setShardTableData] = useState<any[]>([]);
  const [shardSummary, setShardSummary] = useState<any>({});
  const [snapshotTableData, setSnapshotTableData] = useState<any[]>([]);

  // Legend hovers
  const [hoveredSearch, setHoveredSearch] = useState<string | null>(null);
  const [hoveredIndexing, setHoveredIndexing] = useState<string | null>(null);
  const [hoveredSize, setHoveredSize] = useState<string | null>(null);
  const [hoveredBlocks, setHoveredBlocks] = useState<string | null>(null);
  const [hoveredDocs, setHoveredDocs] = useState<string | null>(null);
  const [hoveredShards, setHoveredShards] = useState<string | null>(null);
  const [hoveredRejects, setHoveredRejects] = useState<string | null>(null);
  const [hoveredHeap, setHoveredHeap] = useState<string | null>(null);
  const [hoveredReadWrite, setHoveredReadWrite] = useState<string | null>(null);
  const [hoveredGc, setHoveredGc] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');
    // const interval = minutes > 1440 ? 60 : minutes > 240 ? 15 : 5;

    const baseParams: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) baseParams.client_id = clientId;

    const safeGet = async (url: string, params: any) => {
      try {
        const res = await baseapi.get(url, { params });
        return res.data;
      } catch {
        return null;
      }
    };

    try {
      const [
        resSearch,
        resIndex,
        resSize,
        resCluster,
        resNodes,
        resShards,
        resSnapshots,
      ] = await Promise.all([
        safeGet('/db_monitor_dashboard/graph/es/index_stats', { ...baseParams, metric: 'search_total' }),
        safeGet('/db_monitor_dashboard/graph/es/index_stats', { ...baseParams, metric: 'index_total' }),
        safeGet('/db_monitor_dashboard/graph/es/index_stats', { ...baseParams, metric: 'size_mb' }),
        safeGet('/db_monitor_dashboard/graph/es/cluster_stats', baseParams),
        safeGet('/db_monitor_dashboard/es/nodes', { cluster_id: activeCluster, client_id: clientId }),
        safeGet('/db_monitor_dashboard/es/shards', { cluster_id: activeCluster, client_id: clientId }),
        safeGet('/db_monitor_dashboard/es/snapshots', { cluster_id: activeCluster, client_id: clientId }),
      ]);

      setSearchStats(resSearch?.data || resSearch);
      setIndexStats(resIndex?.data || resIndex);
      setSizeStats(resSize?.data || resSize);
      setClusterStats(resCluster?.data || resCluster);

      setNodeTableData(Array.isArray(resNodes?.data) ? resNodes.data : Array.isArray(resNodes) ? resNodes : []);

      if (resShards?.data && Array.isArray(resShards.data)) {
        setShardTableData(resShards.data);
        setShardSummary(resShards.summary || {});
      } else if (Array.isArray(resShards)) {
        setShardTableData(resShards);
        setShardSummary({});
      } else {
        setShardTableData([]);
        setShardSummary({});
      }

      setSnapshotTableData(Array.isArray(resSnapshots?.data) ? resSnapshots.data : Array.isArray(resSnapshots) ? resSnapshots : []);
    } catch (err) {
      console.warn('OpenSearchIndicesView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Format Time XAxis Labels
  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  // Helper to build pattern charts data
  const buildPatternData = (apiData: any, metric: string, dropIdle = false) => {
    if (!apiData || !apiData.labels || !apiData.databases) {
      return { timeLabels: [], seriesKeys: [], data: [] };
    }
    const MAX_SERIES = 8;
    const timeLabels = formatTimeLabels(apiData.labels);
    const dbs: string[] = apiData.databases || [];
    const seriesObj = apiData.series || {};

    let ranked = dbs
      .map((idx) => {
        const vals: number[] = (seriesObj[`${idx}:${metric}`] || []).filter((v: any) => v != null);
        return { idx, max: vals.length ? Math.max(...vals) : 0 };
      })
      .sort((a, b) => b.max - a.max);

    if (dropIdle) {
      const active = ranked.filter((r) => r.max > 0);
      if (active.length) ranked = active;
    }

    const topPatterns = ranked.slice(0, MAX_SERIES).map((r) => r.idx);

    const data = timeLabels.map((t, idx) => {
      const row: any = { time: t };
      topPatterns.forEach((p) => {
        const arr = seriesObj[`${p}:${metric}`];
        row[p] = arr && arr[idx] != null ? arr[idx] : 0;
      });
      return row;
    });

    return { timeLabels, seriesKeys: topPatterns, data };
  };

  const searchPatternObj = useMemo(() => buildPatternData(searchStats, 'search_total', true), [searchStats, timeRange]);
  const indexPatternObj = useMemo(() => buildPatternData(indexStats, 'index_total', true), [indexStats, timeRange]);
  const sizePatternObj = useMemo(() => buildPatternData(sizeStats, 'size_mb', false), [sizeStats, timeRange]);

  // Helper to build cluster stats data
  const clusterData = useMemo(() => {
    if (!clusterStats || !clusterStats.labels) return { timeLabels: [], data: [] };
    const timeLabels = formatTimeLabels(clusterStats.labels);
    const s = clusterStats.series || {};

    const data = timeLabels.map((t, idx) => ({
      time: t,
      writes_blocked: s.writes_blocked?.[idx] ?? 0,
      master_discovered: s.master_discovered?.[idx] ?? 0,
      nodes_total: s.nodes_total?.[idx] ?? 0,
      fs_free_mb: s.fs_free_mb?.[idx] ?? 0,
      docs_total: s.docs_total?.[idx] ?? 0,
      docs_deleted: s.docs_deleted?.[idx] ?? 0,
      index_count: s.index_count?.[idx] ?? 0,
      shards_active: s.shards_active?.[idx] ?? 0,
      shards_unassigned: s.shards_unassigned?.[idx] ?? 0,
      shards_relocating: s.shards_relocating?.[idx] ?? 0,
      shards_initializing: s.shards_initializing?.[idx] ?? 0,
      shards_delayed_unassigned: s.shards_delayed_unassigned?.[idx] ?? 0,
      search_reject_rate: s.search_reject_rate?.[idx] ?? 0,
      write_reject_rate: s.write_reject_rate?.[idx] ?? 0,
      jvm_heap_pct: s.jvm_heap_pct?.[idx] ?? 0,
      jvm_heap_max_pct: s.jvm_heap_max_pct?.[idx] ?? 0,
      search_rate: s.search_rate?.[idx] ?? 0,
      indexing_rate: s.indexing_rate?.[idx] ?? 0,
      gc_young_ms_rate: s.gc_young_ms_rate?.[idx] ?? 0,
      gc_old_ms_rate: s.gc_old_ms_rate?.[idx] ?? 0,
      pending_tasks: s.pending_tasks?.[idx] ?? 0,
    }));

    return { timeLabels, data };
  }, [clusterStats, timeRange]);

  const hasChartData = (arr: any[], key?: string) => {
    if (!arr || arr.length === 0) return false;
    if (!key) return true;
    return arr.some((item) => typeof item[key] === 'number' && !isNaN(item[key]));
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  // Format age helper
  const fmtAge = (sec: number) => {
    if (!sec) return '—';
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const fmtDur = (sec: number) => {
    if (!sec) return '0s';
    if (sec >= 3600) return `${(sec / 3600).toFixed(1)}h`;
    if (sec >= 60) return `${(sec / 60).toFixed(1)}m`;
    return `${sec}s`;
  };

  const shardSummaryText = useMemo(() => {
    const parts = Object.keys(shardSummary).sort().map(s => `${shardSummary[s]} ${s}`);
    return parts.length ? parts.join(' · ') : 'no shards';
  }, [shardSummary]);  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">

      {/* Page Header */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Layers className="w-5 h-5 text-orange-500" />
          OpenSearch / ES Indices & Cluster Status
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          At-a-glance node health, storage capacity, shard placement, snapshot history, and index activity breakdowns.
        </p>
      </div>

      {/* SECTION 1: NODE HEALTH TABLE */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4 text-orange-500" />
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200">Node Health</h3>
          </div>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600/50 text-gray-700 dark:text-gray-300">
            {nodeTableData.length} node{nodeTableData.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left border-collapse text-xs font-mono">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700/60 text-gray-500 dark:text-gray-400 text-[10px] uppercase tracking-wider bg-gray-50/50 dark:bg-transparent">
                <th className="py-2 px-2.5">Node</th>
                <th className="py-2 px-2.5">Role</th>
                <th className="py-2 px-2.5 text-right">Heap</th>
                <th className="py-2 px-2.5 text-right">Disk</th>
                <th className="py-2 px-2.5 text-right">ES CPU</th>
                <th className="py-2 px-2.5 text-right">Host CPU</th>
                <th className="py-2 px-2.5 text-right">Conns</th>
                <th className="py-2 px-2.5 text-right">Rejections (S/W)</th>
                <th className="py-2 px-2.5 text-right">Uptime</th>
                <th className="py-2 px-2.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800/60 text-gray-800 dark:text-gray-200">
              {nodeTableData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-6 text-center text-gray-500 italic">
                    No node telemetry data available.
                  </td>
                </tr>
              ) : (
                nodeTableData.map((node, idx) => {
                  const heapPct = node.heap_pct;
                  const diskPct = node.disk_used_pct;
                  const heapClass = heapPct >= 85 ? 'text-red-500 dark:text-red-400 font-bold' : heapPct >= 75 ? 'text-amber-500 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
                  const diskClass = diskPct >= 90 ? 'text-red-500 dark:text-red-400 font-bold' : diskPct >= 80 ? 'text-amber-500 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
                  const isHealthy = node.health === 'green';
                  const isPressure = node.health === 'yellow';

                  return (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                      <td className="py-2 px-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-gray-900 dark:text-white text-xs">{node.node || '—'}</span>
                          {node.is_master && (
                            <span className="px-1.5 py-0.2 rounded text-[8px] font-bold uppercase bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-500/30">
                              MASTER
                            </span>
                          )}
                        </div>
                        <div className="text-[9px] text-gray-500 dark:text-gray-400 font-mono">
                          {node.ip || ''} {node.version ? `· v${node.version}` : ''}
                        </div>
                      </td>
                      <td className="py-2 px-2.5 text-gray-600 dark:text-gray-400">{node.role || '—'}</td>
                      <td className={`py-2 px-2.5 text-right ${heapClass}`}>
                        {heapPct != null ? `${heapPct.toFixed(1)}%` : '—'}
                      </td>
                      <td className={`py-2 px-2.5 text-right ${diskClass}`}>
                        {diskPct != null ? `${diskPct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-700 dark:text-gray-300">
                        {node.cpu_pct != null ? `${node.cpu_pct.toFixed(0)}%` : '—'}
                      </td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-700 dark:text-gray-300">
                        {node.host_cpu_pct != null ? `${node.host_cpu_pct.toFixed(0)}%` : '—'}
                      </td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-700 dark:text-gray-300">{node.connections ?? '—'}</td>
                      <td className="py-2 px-2.5 text-right font-mono">
                        <span className={(node.search_rejects || node.write_rejects) ? 'text-red-500 dark:text-red-400 font-bold' : 'text-gray-500 dark:text-gray-400'}>
                          {node.search_rejects ?? 0} / {node.write_rejects ?? 0}
                        </span>
                      </td>
                      <td className="py-2 px-2.5 text-right text-gray-500 dark:text-gray-400 text-[10px]">{fmtAge(node.uptime_sec || 0)}</td>
                      <td className="py-2 px-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border ${isHealthy
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                            : isPressure
                              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                              : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                          }`}>
                          {isHealthy ? 'HEALTHY' : isPressure ? 'PRESSURE' : 'DOWN'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION 2: CLUSTER CAPACITY & HEALTH CHARTS */}
      <div className="space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <Activity className="w-4 h-4 text-orange-500" />
          Cluster Capacity & Health Metrics
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Write Blocks & Master Reachability */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-red-500 dark:text-red-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Write Blocks & Master Reachability</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">1 = Yes</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 1]} ticks={[0, 1]} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredBlocks} onHoverSeries={setHoveredBlocks} />} />
                    <Line
                      type="stepAfter"
                      dataKey="writes_blocked"
                      name="Writes Blocked"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredBlocks, 'writes_blocked')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      type="stepAfter"
                      dataKey="master_discovered"
                      name="Master Discovered"
                      stroke="#10b981"
                      strokeOpacity={getOpacity(hoveredBlocks, 'master_discovered')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No block stats available</span>
              )}
            </div>
          </div>

          {/* Total Nodes */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Total Nodes</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">nodes</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasChartData(clusterData.data, 'nodes_total') ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Line type="monotone" dataKey="nodes_total" name="Nodes" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No node stats available</span>
              )}
            </div>
          </div>

          {/* Total Free Storage Space (MB) */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Total Free Storage Space</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">MB</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasChartData(clusterData.data, 'fs_free_mb') ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clusterData.data}>
                    <defs>
                      <linearGradient id="colorFreeStorage" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Area type="monotone" dataKey="fs_free_mb" name="Free Storage (MB)" stroke="#06b6d4" fill="url(#colorFreeStorage)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No free storage data</span>
              )}
            </div>
          </div>

          {/* Documents — Total & Deleted */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Documents — Total & Deleted</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">docs</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clusterData.data}>
                    <defs>
                      <linearGradient id="colorDocsTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="colorDocsDeleted" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredDocs={hoveredDocs} onHoverSeries={setHoveredDocs} />} />
                    <Area
                      type="monotone"
                      dataKey="docs_total"
                      name="Total Docs"
                      stroke="#a855f7"
                      fill="url(#colorDocsTotal)"
                      strokeOpacity={getOpacity(hoveredDocs, 'docs_total')}
                      fillOpacity={getOpacity(hoveredDocs, 'docs_total') * 0.3}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="docs_deleted"
                      name="Deleted Docs"
                      stroke="#ef4444"
                      fill="url(#colorDocsDeleted)"
                      strokeOpacity={getOpacity(hoveredDocs, 'docs_deleted')}
                      fillOpacity={getOpacity(hoveredDocs, 'docs_deleted') * 0.3}
                      strokeWidth={1.5}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No doc count stats available</span>
              )}
            </div>
          </div>

          {/* Read vs Write Rate */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl md:col-span-2">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Read vs Write Rate</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">ops/s</span>
            </div>
            <div className="h-44 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredReadWrite} onHoverSeries={setHoveredReadWrite} />} />
                    <Line
                      type="monotone"
                      dataKey="search_rate"
                      name="Reads/s (search)"
                      stroke="#10b981"
                      strokeOpacity={getOpacity(hoveredReadWrite, 'search_rate')}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="indexing_rate"
                      name="Writes/s (index)"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredReadWrite, 'indexing_rate')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No read/write rate data available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: INDICES & SHARDS ACTIVITY CHARTS */}
      <div className="space-y-2 pt-2">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <Layers className="w-4 h-4 text-orange-500 dark:text-orange-400" />
          Indices & Shards Activity
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search Rate by Index Pattern */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Search Rate by Index Pattern</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">queries/s</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {searchPatternObj.seriesKeys.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={searchPatternObj.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredSearch} onHoverSeries={setHoveredSearch} />} />
                    {searchPatternObj.seriesKeys.map((pattern, i) => (
                      <Line
                        key={pattern}
                        type="monotone"
                        dataKey={pattern}
                        name={pattern}
                        stroke={PALETTE[i % PALETTE.length]}
                        strokeOpacity={getOpacity(hoveredSearch, pattern)}
                        strokeWidth={hoveredSearch === pattern ? 2.5 : 1.5}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No index search rate data available</span>
              )}
            </div>
          </div>

          {/* Indexing Rate by Index Pattern */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Indexing Rate by Index Pattern</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">docs/s</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {indexPatternObj.seriesKeys.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={indexPatternObj.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredIndexing} onHoverSeries={setHoveredIndexing} />} />
                    {indexPatternObj.seriesKeys.map((pattern, i) => (
                      <Line
                        key={pattern}
                        type="monotone"
                        dataKey={pattern}
                        name={pattern}
                        stroke={PALETTE[i % PALETTE.length]}
                        strokeOpacity={getOpacity(hoveredIndexing, pattern)}
                        strokeWidth={hoveredIndexing === pattern ? 2.5 : 1.5}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No indexing rate data available</span>
              )}
            </div>
          </div>

          {/* Storage by Index Pattern */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Storage by Index Pattern</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">MB</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {sizePatternObj.seriesKeys.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sizePatternObj.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredSize} onHoverSeries={setHoveredSize} />} />
                    {sizePatternObj.seriesKeys.map((pattern, i) => (
                      <Line
                        key={pattern}
                        type="monotone"
                        dataKey={pattern}
                        name={pattern}
                        stroke={PALETTE[i % PALETTE.length]}
                        strokeOpacity={getOpacity(hoveredSize, pattern)}
                        strokeWidth={hoveredSize === pattern ? 2.5 : 1.5}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No storage size data available</span>
              )}
            </div>
          </div>

          {/* Shards States */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Network className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Shard States</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">shards</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredShards} onHoverSeries={setHoveredShards} />} />
                    <Line
                      type="monotone"
                      dataKey="shards_active"
                      name="Active"
                      stroke="#10b981"
                      strokeOpacity={getOpacity(hoveredShards, 'shards_active')}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="shards_unassigned"
                      name="Unassigned"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredShards, 'shards_unassigned')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="shards_relocating"
                      name="Relocating"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredShards, 'shards_relocating')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="shards_initializing"
                      name="Initializing"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredShards, 'shards_initializing')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="shards_delayed_unassigned"
                      name="Delayed"
                      stroke="#a855f7"
                      strokeOpacity={getOpacity(hoveredShards, 'shards_delayed_unassigned')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No shard metrics available</span>
              )}
            </div>
          </div>

          {/* Thread-pool Rejections */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Thread-pool Rejections</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">rejects/s</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredRejects} onHoverSeries={setHoveredRejects} />} />
                    <Line
                      type="monotone"
                      dataKey="search_reject_rate"
                      name="Search rejects/s"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredRejects, 'search_reject_rate')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="write_reject_rate"
                      name="Write rejects/s"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredRejects, 'write_reject_rate')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No thread-pool rejection data</span>
              )}
            </div>
          </div>

          {/* JVM Heap Utilization */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">JVM Heap Utilization</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">%</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clusterData.data}>
                    <defs>
                      <linearGradient id="colorJvmHeap" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredHeap} onHoverSeries={setHoveredHeap} />} />
                    <Area
                      type="monotone"
                      dataKey="jvm_heap_pct"
                      name="Avg heap %"
                      stroke="#10b981"
                      fill="url(#colorJvmHeap)"
                      strokeOpacity={getOpacity(hoveredHeap, 'jvm_heap_pct')}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="jvm_heap_max_pct"
                      name="Hottest node %"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredHeap, 'jvm_heap_max_pct')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No JVM heap stats available</span>
              )}
            </div>
          </div>

          {/* GC Time */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Recycle className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">GC Time</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">ms of GC per second</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {hasChartData(clusterData.data) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredGc} onHoverSeries={setHoveredGc} />} />
                    <Line
                      type="monotone"
                      dataKey="gc_young_ms_rate"
                      name="Young GC ms/s"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredGc, 'gc_young_ms_rate')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="gc_old_ms_rate"
                      name="Old GC ms/s"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredGc, 'gc_old_ms_rate')}
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No GC time telemetry available</span>
              )}
            </div>
          </div>

          {/* Pending Cluster Tasks */}
          <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
            <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                <h4 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">Pending Cluster Tasks</h4>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">tasks</span>
            </div>
            <div className="h-48 w-full flex items-center justify-center">
              {hasChartData(clusterData.data, 'pending_tasks') ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clusterData.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#888888" opacity={0.15} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Bar dataKey="pending_tasks" name="Pending Tasks" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No pending tasks data available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 4: SHARD PLACEMENT TABLE */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <Network className="w-4 h-4 text-orange-500" />
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200">Shard Placement</h3>
          </div>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600/50 text-gray-700 dark:text-gray-300">
            {shardSummaryText}
          </span>
        </div>

        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left border-collapse text-xs font-mono">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700/60 text-gray-500 dark:text-gray-400 text-[10px] uppercase tracking-wider bg-gray-50/50 dark:bg-transparent">
                <th className="py-2 px-2.5">Index</th>
                <th className="py-2 px-2.5 text-right">Shard</th>
                <th className="py-2 px-2.5">Type</th>
                <th className="py-2 px-2.5">State</th>
                <th className="py-2 px-2.5">Node</th>
                <th className="py-2 px-2.5 text-right">Docs</th>
                <th className="py-2 px-2.5 text-right">Size</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800/60 text-gray-800 dark:text-gray-200">
              {shardTableData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-gray-500 italic">
                    No shard placement telemetry available.
                  </td>
                </tr>
              ) : (
                shardTableData.slice(0, 40).map((shardItem, idx) => {
                  const state = shardItem.state || '';
                  const isStarted = state === 'STARTED';
                  const isTransitioning = state === 'RELOCATING' || state === 'INITIALIZING';
                  const isPrimary = shardItem.shard_type === 'primary';

                  const sizeMb = shardItem.size_mb || 0;
                  const sizeFormatted = sizeMb >= 1024 ? `${(sizeMb / 1024).toFixed(2)} GB` : `${sizeMb.toFixed(2)} MB`;

                  return (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                      <td className="py-2 px-2.5 font-bold text-gray-900 dark:text-white text-xs">{shardItem.index || '—'}</td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-700 dark:text-gray-300">{shardItem.shard ?? '—'}</td>
                      <td className={`py-2 px-2.5 ${isPrimary ? 'text-orange-500 dark:text-orange-400 font-bold' : 'text-gray-600 dark:text-gray-400'}`}>
                        {shardItem.shard_type || '—'}
                      </td>
                      <td className="py-2 px-2.5">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border ${isStarted
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                            : isTransitioning
                              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                              : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                          }`}>
                          {state || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="py-2 px-2.5 text-gray-600 dark:text-gray-400">{shardItem.node || '—'}</td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-700 dark:text-gray-300">
                        {(shardItem.docs || 0).toLocaleString()}
                      </td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-600 dark:text-gray-400">{sizeFormatted}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION 5: SNAPSHOTS TABLE */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-orange-500" />
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-200">Snapshots</h3>
          </div>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600/50 text-gray-700 dark:text-gray-300">
            {snapshotTableData.length} snapshot{snapshotTableData.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left border-collapse text-xs font-mono">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700/60 text-gray-500 dark:text-gray-400 text-[10px] uppercase tracking-wider bg-gray-50/50 dark:bg-transparent">
                <th className="py-2 px-2.5">Repository</th>
                <th className="py-2 px-2.5">Snapshot</th>
                <th className="py-2 px-2.5 text-center">Status</th>
                <th className="py-2 px-2.5 text-right">Shards (Succ / Fail / Total)</th>
                <th className="py-2 px-2.5 text-right">Duration</th>
                <th className="py-2 px-2.5 text-right">End Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800/60 text-gray-800 dark:text-gray-200">
              {snapshotTableData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-gray-500 italic">
                    No snapshots found — no repository configured or none taken yet.
                  </td>
                </tr>
              ) : (
                snapshotTableData.map((snap, idx) => {
                  const status = snap.status || '';
                  const isSuccess = status === 'SUCCESS';
                  const isRunning = status === 'IN_PROGRESS';
                  const isPartial = status === 'PARTIAL';

                  const endTimeStr = snap.end_epoch ? new Date(snap.end_epoch * 1000).toLocaleString() : '—';

                  return (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                      <td className="py-2 px-2.5 text-gray-600 dark:text-gray-400">{snap.repository || '—'}</td>
                      <td className="py-2 px-2.5 font-bold text-gray-900 dark:text-white text-xs">{snap.snapshot || '—'}</td>
                      <td className="py-2 px-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase border ${isSuccess
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                            : isRunning || isPartial
                              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                              : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
                          }`}>
                          {status || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="py-2 px-2.5 text-right font-mono">
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">{snap.successful_shards ?? 0}</span> /{' '}
                        <span className={(snap.failed_shards || 0) > 0 ? 'text-red-500 dark:text-red-400 font-bold' : 'text-gray-500 dark:text-gray-400'}>
                          {snap.failed_shards ?? 0}
                        </span> / {snap.total_shards ?? 0}
                      </td>
                      <td className="py-2 px-2.5 text-right font-mono text-gray-700 dark:text-gray-300">{fmtDur(snap.duration_sec || 0)}</td>
                      <td className="py-2 px-2.5 text-right text-gray-500 dark:text-gray-400 text-[10px]">{endTimeStr}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OpenSearchIndicesView;

